/**
 * ProductsLibrary — the admin-only Products catalog surface.
 *
 * Mirrors the Services / Messages library layout: stat tiles, list/card view
 * toggle, search + category + status filters, and the standard row/card action
 * cluster (view · edit · duplicate · ⋯ with audit trail). Products come from an
 * external import feed (interim seed in `useProducts`) and can be attached to
 * events and appointment/queue services.
 */
import { useState, type CSSProperties } from "react";
const BADGE_GLYPH_RATIO = 0.45; // ui-config — icon glyph scales with its badge box (presentational ratio, not data)
import { ChipToggleGroup, Btn, Card, Icon, Input, Pill, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { StatTile, FilterBar, EmptyState, ExplainerFooter, FilterDropdown, RowActions } from "../../shell/composites";
import { useViewMode, ViewToggle, CARD_GRID, CardActionBar } from "../../shell/list-composites";
import { can } from "../../shell/access";
import { useNav } from "../../shell/router";
import { toast } from "../../shell/toast";
import { useProducts, productPriceLabel, productAuthoringAvailable, PRODUCT_CATEGORIES, type Product } from "./useProducts";
import { useT, t as i18nT } from "../../lib/i18n";

const th: CSSProperties = { padding: "10px 16px", textAlign: "left", fontWeight: 600, whiteSpace: "nowrap", fontSize: 11, textTransform: "uppercase", color: T.soft };
const td: CSSProperties = { padding: "12px 16px", verticalAlign: "middle", color: T.n2 };

const statusPill = (s: string) =>
  s === "active" ? <Pill tone="success" dot>{i18nT("common.status.active", "Active")}</Pill> : <Pill tone="neutral" dot>{i18nT("common.status.inactive", "Inactive")}</Pill>;

function ProductThumb({ p, size = 34 }: { p: Product; size?: number }) {
  if (p.imageUrl) return <img src={p.imageUrl} alt="" style={{ width: size, height: size, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />;
  return (
    <span style={{ width: size, height: size, borderRadius: 8, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <Icon.tags size={size * BADGE_GLYPH_RATIO} />
    </span>
  );
}

export default function ProductsLibrary() {
  const t = useT();
  const nav = useNav();
  const store = useProducts();
  const [view, setView] = useViewMode("products:view");
  const [query, setQuery] = useState("");
  const [catFilter, setCatFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const canWrite = can("products.write");
  // Authoring (create/edit fields/duplicate/status/remove) waits on the REQ-027
  // ops — until they ship, those affordances are hidden rather than faking
  // persistence. Associations (attach to events/services) are live regardless.
  const authoring = productAuthoringAvailable();

  const products = store.products;
  const rows = products.filter((p) => {
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    if (catFilter && p.category !== catFilter) return false;
    if (query.trim() && !`${p.name} ${p.sku} ${p.category} ${p.vendor || ""}`.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });
  const active = products.filter((p) => p.status === "active").length;
  const linked = products.filter((p) => p.serviceIds.length || p.eventIds.length).length;

  const dup = async (p: Product) => { const c = await store.duplicate(p.id); if (c) toast({ tone: "success", title: t("products.screens.productDuplicated", "Product duplicated"), subtitle: i18nT("products.screens.cNameAddedAsInactive.icu", "\"{value0}\" added as inactive.", { value0: c.name }) }); else toast({ tone: "danger", title: t("products.screens.couldnTDuplicate", "Couldn't duplicate"), subtitle: t("products.screens.nothingWasChangedRetryInAMoment", "Nothing was changed. Retry in a moment.") }); };
  const del = async (p: Product) => { const ok = await store.remove(p.id).catch(() => false); toast(ok ? { tone: "warn", title: t("products.screens.productRemoved", "Product removed") } : { tone: "danger", title: t("products.screens.couldnTRemove", "Couldn't remove"), subtitle: t("products.screens.nothingWasChangedRetryInAMoment", "Nothing was changed. Retry in a moment.") }); };
  const toggle = async (p: Product) => { const next = p.status === "active" ? "inactive" : "active"; const ok = await store.setStatus(p.id, next).catch(() => false); toast(ok ? { tone: next === "active" ? "success" : "warn", title: next === "active" ? "Product activated" : "Product deactivated" } : { tone: "danger", title: t("products.screens.couldnTUpdateStatus", "Couldn't update status"), subtitle: t("products.screens.nothingWasChangedRetryInAMoment", "Nothing was changed. Retry in a moment.") }); };

  const assocLabel = (p: Product) => {
    const s = p.serviceIds.length, e = p.eventIds.length;
    if (!s && !e) return "Not linked";
    const parts = [];
    if (e) parts.push(`${e} event${e === 1 ? "" : "s"}`);
    if (s) parts.push(`${s} service${s === 1 ? "" : "s"}`);
    return parts.join(" · ");
  };

  const actions = (p: Product) => ({
    testid: "products-item",
    onView: () => nav.go("products.edit", { id: p.id, view: 1 }),
    viewLabel: "View product",
    onEdit: canWrite ? () => nav.go("products.edit", { id: p.id }) : undefined,
    editLabel: authoring ? "Edit product" : "Edit attachments",
    onDuplicate: canWrite && authoring ? () => dup(p) : undefined,
    audit: { type: "product" as const, id: p.id, name: p.name },
    moreHeader: p.name,
    moreItems: canWrite && authoring ? [
      p.status === "active"
        ? { icon: <Icon.refresh size={14} />, label: t("products.screens.deactivate", "Deactivate"), onClick: () => toggle(p) }
        : { icon: <Icon.check size={14} />, label: t("products.screens.activate", "Activate"), onClick: () => toggle(p) },
      "divider" as const,
      { icon: <Icon.trash size={14} />, label: t("products.screens.removeProduct", "Remove product"), danger: true, sub: "Also unlinks it everywhere", onClick: () => del(p) },
    ] : [],
  });

  const anyFilter = catFilter || query.trim() || statusFilter !== "all";
  const statusChips: [string, string][] = [["all", `All · ${products.length}`], ["active", `Active · ${active}`], ["inactive", `Inactive · ${products.length - active}`]];

  return (
    <Screen rail="products">
      <PageHeader
        title={t("products.screens.products", "Products")}
        subtitle={t("products.screens.anExternalCatalogOfProductsYouCanAttachToEventsAppointmentsAnd", "An external catalog of products you can attach to events, appointments and queue services.")}
        breadcrumb={null}
        actions={<>
          <ViewToggle mode={view} onChange={setView} />
          {canWrite && authoring ? <Btn icon={<Icon.plus />} onClick={() => nav.go("products.edit", { new: 1 })}>{t("products.screens.newProduct", "New product")}</Btn> : null}
        </>}
      />
      <div style={{ padding: "16px 24px 0", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        <StatTile label={t("products.screens.products", "Products")} value={products.length} icon={<Icon.tags size={14} />} accent={T.p1} />
        <StatTile label={t("common.status.active", "Active")} value={active} icon={<Icon.check size={14} />} accent={T.green1} />
        <StatTile label={t("products.screens.linked", "Linked")} value={linked} icon={<Icon.layers size={14} />} accent={T.tDarkBlue} />
        <StatTile label={t("products.screens.categories", "Categories")} value={new Set(products.map((p) => p.category)).size} icon={<Icon.grid size={14} />} accent={T.tPurple} />
      </div>

      <FilterBar>
        <Input full={false} leading={<Icon.search size={14} />} placeholder={t("products.screens.searchProducts", "Search products…")} value={query} onChange={(e) => setQuery(e.target.value)} size="sm" style={{ width: 260 }} />
        <span style={{ width: 1, height: 20, background: T.border, margin: "0 4px" }} />
        <ChipToggleGroup
          ariaLabel={t("products.screens.ariaLabel", "Status filter")}
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as string)}
          options={statusChips.map(([id, lbl]) => ({ value: id, label: lbl }))}
        />
        <span style={{ width: 1, height: 20, background: T.border, margin: "0 4px" }} />
        <FilterDropdown label={catFilter || t("products.productsScreens.anyCategory", "Any category")} active={!!catFilter} options={[...PRODUCT_CATEGORIES]} onPick={setCatFilter} onClear={() => setCatFilter(null)} />
        {anyFilter ? <Btn size="sm" variant="link" onClick={() => { setCatFilter(null); setQuery(""); setStatusFilter("all"); }}>{t("common.actions.clearAll", "Clear all")}</Btn> : null}
        <span style={{ marginLeft: "auto", fontSize: 12, color: T.soft }}>{rows.length} {t("products.screens.of", "of")}{" "}{products.length}</span>
      </FilterBar>

      <div style={{ flex: 1, overflow: "auto", padding: "0 24px 24px" }}>
        {rows.length === 0 ? (
          <EmptyState variant="card" icon={<Icon.tags size={20} />} title={products.length === 0 ? t("products.productsScreens.noProductsYet", "No products yet") : t("products.productsScreens.noProductsMatch", "No products match")} body={products.length === 0 ? (authoring ? t("products.screens.connectAProductImportIntegrationOrAddAProductManually", "Connect a product-import integration, or add a product manually.") : t("products.screens.connectAProductImportIntegrationToFillTheCatalog", "Connect a product-import integration to fill the catalog.")) : t("products.screens.tryClearingAFilter", "Try clearing a filter.")}
            action={canWrite && authoring ? <Btn icon={<Icon.plus size={14} />} onClick={() => nav.go("products.edit", { new: 1 })}>{t("products.screens.newProduct", "New product")}</Btn> : null} />
        ) : view === "grid" ? (
          <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: 16 }}>
            {rows.map((p) => (
              <Card key={p.id} onClick={() => actions(p).onView()} style={{ padding: 0, cursor: "pointer", overflow: "hidden", display: "flex", flexDirection: "column", borderLeft: `3px solid ${p.status === "active" ? T.green1 : T.n4}` }}
                onMouseEnter={(e) => (e.currentTarget.style.boxShadow = T.shadowMd)} onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}>
                <div style={{ padding: "16px 18px 14px", flex: 1, display: "flex", flexDirection: "column", gap: 11 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <ProductThumb p={p} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 14.5, fontWeight: 700, color: T.n1 }}>{p.name}</div>
                      <div style={{ fontSize: 12, color: T.soft, marginTop: 2, fontFamily: T.fontMono }}>{p.sku}</div>
                    </div>
                    {statusPill(p.status)}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <Pill tone="neutral">{p.category}</Pill>
                    <span style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{productPriceLabel(p)}</span>
                  </div>
                  {p.vendor ? <div style={{ fontSize: 12, color: T.soft }}>{p.vendor}</div> : null}
                </div>
                <CardActionBar left={<span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Icon.layers size={12} /> {assocLabel(p)}</span>} {...actions(p)} />
              </Card>
            ))}
          </div>
        ) : (
          <Card style={{ padding: 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font, fontSize: 13 }}>
              <thead>
                <tr style={{ background: T.n6 }}>
                  <th style={th}>{t("products.screens.product", "Product")}</th><th style={th}>{t("products.screens.sku", "SKU")}</th><th style={th}>{t("products.screens.category", "Category")}</th><th style={th}>{t("products.screens.price", "Price")}</th><th style={th}>{t("products.screens.linkedTo", "Linked to")}</th><th style={th}>{t("products.screens.status", "Status")}</th><th style={{ ...th, width: 150, textAlign: "right" }}>{t("products.screens.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p, i) => (
                  <tr key={p.id} onClick={() => actions(p).onView()} style={{ borderTop: i ? `1px solid ${T.border}` : "none", cursor: "pointer", borderLeft: `3px solid ${p.status === "active" ? T.green1 : T.n4}` }}>
                    <td style={td}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <ProductThumb p={p} size={30} />
                        <div style={{ color: T.n1, fontWeight: 600 }}>{p.name}</div>
                      </div>
                    </td>
                    <td style={{ ...td, fontFamily: T.fontMono, fontSize: 12, color: T.soft }}>{p.sku}</td>
                    <td style={td}>{p.category}</td>
                    <td style={{ ...td, color: T.n1, fontWeight: 600 }}>{productPriceLabel(p)}</td>
                    <td style={{ ...td, fontSize: 12.5 }}>{assocLabel(p)}</td>
                    <td style={td}>{statusPill(p.status)}</td>
                    <td style={td} onClick={(e) => e.stopPropagation()}><RowActions {...actions(p)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
        <ExplainerFooter>
          <b>{t("products.screens.products", "Products")}</b> {t("products.screens.areAnExternalCatalogImportedViaA", "are an external catalog imported via a")}{" "}<b>{t("products.screens.productImports", "Product imports")}</b> {t("products.screens.integrationAttachAProductToAn", "integration. Attach a product to an")}{" "}<b>{t("products.screens.event", "event")}</b>{t("products.screens.orToAn", ", or to an")}{" "}<b>{t("products.screens.appointment", "appointment")}</b> {t("products.screens.or", "or")}{" "}<b>{t("products.screens.queue", "queue")}</b> {t("products.screens.serviceOneProductCanBeLinkedToMany", "service — one product can be linked to many.")}</ExplainerFooter>
      </div>
    </Screen>
  );
}
